/**
 * Copyright (c) 2016-present GlossGenius. All rights reserved.
 */

import * as React from 'react';

import cx from 'classnames';
import { SocialMediaLinks } from 'themes/common/components/NavItemSocial/SocialMediaLinks';

import MenuIcon from 'components/Icons/NavButton.svg';

import { useNavigation } from 'hooks/navigation/useNavigation';

import { BagCounter } from 'routes/shop/bag/components/BagCounter/BagCounter';

import GGLogo from '../../../../../public/assets/logos/GG_Logo_White.svg';
import { LegalItem } from './LegalItem';
import { NavItem } from './NavItem';
import { NavItemHome } from './NavItemHome';

import s from './Navigation.module.css';

const Navigation = () => {
  const {
    isMenuOpen,
    showTeam,
    showPortfolio,
    showShopLink,
    aboutUrl,
    teamUrl,
    servicesUrl,
    portfolioUrl,
    contactUrl,
    bookUrl,
    bagUrl,
    isPackagePurchaseEnabled,
    packagesUrl,
    membershipsUrl,
    giftCardsUrl,
    shopSubNavigationItems,
    termsOfUseUrl,
    privacyPolicyUrl,
    shopUrl,
    renderOverlay,
    renderMenuButton,
    tracking,
    renderMenuContainer,
    navigationRef,
    socialMediaUrls,
  } = useNavigation({
    styles: {
      overlay: s.overlay,
      overlayOpen: s.overlayOpen,
      overlayClosed: s.overlayClosed,
      menuButton: s.button,
      menuContainer: s.menuContainer,
      menuContainerOpen: s.menuContainerOpen,
      menuContainerClosed: s.menuContainerClosed,
    },
  });

  return (
    <nav className={s.root} ref={navigationRef} aria-label="main navigation">
      {renderOverlay()}
      <ul className={cx(s.desktopNav, s.menu)}>
        <NavItem to={aboutUrl}>About</NavItem>
        {showTeam && (
          <NavItem to={teamUrl}>
            <span data-testid="our-team">
              <span className={s.fullText}>Our Team</span>
              <span className={s.minimalText}>Team</span>
            </span>
          </NavItem>
        )}
        <NavItem to={servicesUrl}>Services</NavItem>
        {showPortfolio && <NavItem to={portfolioUrl}>Portfolio</NavItem>}
        {packagesUrl && !isPackagePurchaseEnabled && (
          <NavItem to={packagesUrl}>Packages</NavItem>
        )}
        {isPackagePurchaseEnabled && (
          <NavItem subNavItems={shopSubNavigationItems}>Shop</NavItem>
        )}
        <NavItem to={contactUrl}>Contact</NavItem>
        {isPackagePurchaseEnabled && (
          <NavItem to={bagUrl} className={s.bagItem} ariaLabel="Bag preview">
            <BagCounter size={24} />
          </NavItem>
        )}
        <NavItem className={s.bookNowCTA} to={bookUrl}>
          <span data-testid="book-now">
            <span className={s.fullText}>Book Now</span>
            <span className={s.minimalText}>Book</span>
          </span>
        </NavItem>
        {showShopLink && (
          <NavItem newTab to={shopUrl}>
            <span className={s.fullText}>Shop Now</span>
            <span className={s.minimalText}>Shop</span>
          </NavItem>
        )}

        <SocialMediaLinks
          className={cx(s.navItem, s.iconLinks)}
          urls={socialMediaUrls}
        />
      </ul>
      <ul className={cx(s.desktopNav, s.legal)}>
        <LegalItem to={termsOfUseUrl}>Terms</LegalItem>
        <LegalItem to={privacyPolicyUrl}>Privacy Policy</LegalItem>
      </ul>
      {renderMenuButton({ content: <MenuIcon /> })}
      {renderMenuContainer({
        isOpen: isMenuOpen,
        content: (
          <>
            <div className={s.menuHeading}>
              <NavItemHome />
            </div>
            <ul className={cx(s.mobileNav, s.menu)}>
              <NavItem
                onClick={() => tracking.navItemClicked('about')}
                className={s.aboutPage}
                to={aboutUrl}
              >
                About
              </NavItem>
              {showTeam && (
                <NavItem
                  onClick={() => tracking.navItemClicked('team')}
                  className={s.teamPage}
                  to={teamUrl}
                >
                  <span data-testid="our-team">
                    <span className={s.fullText}>Our Team</span>
                    <span className={s.minimalText}>Team</span>
                  </span>
                </NavItem>
              )}
              <NavItem
                onClick={() => tracking.navItemClicked('services')}
                className={cx(
                  !showTeam ? s.servicesWithNoTeam : s.servicesPage,
                )}
                to={servicesUrl}
              >
                Services
              </NavItem>

              {showPortfolio && (
                <NavItem
                  onClick={() => tracking.navItemClicked('portfolio')}
                  className={s.portfolioPage}
                  to={portfolioUrl}
                >
                  Portfolio
                </NavItem>
              )}
              {packagesUrl && (
                <NavItem
                  onClick={() => tracking.navItemClicked('packages')}
                  className={s.packagesPage}
                  to={packagesUrl}
                >
                  Packages
                </NavItem>
              )}
              {membershipsUrl && (
                <NavItem
                  onClick={() => tracking.navItemClicked('memberships')}
                  to={membershipsUrl}
                >
                  Memberships
                </NavItem>
              )}
              {giftCardsUrl && (
                <NavItem
                  onClick={() => tracking.navItemClicked('gift-cards')}
                  to={giftCardsUrl}
                >
                  Gift Cards
                </NavItem>
              )}
              <NavItem
                onClick={() => tracking.navItemClicked('contact')}
                className={cx(
                  showPortfolio
                    ? s.contactsPagePortfolioPersist
                    : s.contactsPage,
                )}
                to={contactUrl}
              >
                Contact
              </NavItem>
              <NavItem
                onClick={() => tracking.navItemClicked('book')}
                className={s.bookNowCTA}
                to={bookUrl}
              >
                <span data-testid="book-now">
                  <span className={s.fullText}>Book Now</span>
                  <span className={s.minimalText}>Book</span>
                </span>
              </NavItem>
              {showShopLink && (
                <NavItem
                  onClick={() => tracking.navItemClicked('shop')}
                  className={s.shopNowCTA}
                  newTab
                  to={shopUrl}
                >
                  <span className={s.fullText}>Shop Now</span>
                  <span className={s.minimalText}>Shop</span>
                </NavItem>
              )}

              <li>
                <SocialMediaLinks
                  className={cx(s.navItem, s.iconLinks)}
                  urls={socialMediaUrls}
                />
              </li>
            </ul>
            <ul className={cx(s.mobileNav, s.legal)}>
              <LegalItem to={termsOfUseUrl}>Terms</LegalItem>
              <LegalItem to={privacyPolicyUrl}>Privacy Policy</LegalItem>
            </ul>
            <div className={cx(s.copyright)}>
              <span className={s.poweredBy}>Powered by </span>
              <GGLogo width="125" height="13" />
            </div>
          </>
        ),
      })}
    </nav>
  );
};

export { Navigation };
